import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowDownCircle,
  Ban,
  Eye,
  MousePointer,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  RefreshCw,
} from "lucide-react";
import { colors, fonts } from "../styles/theme";
import { fetchEmailActivity } from "../services/api";
import { Badge } from "../components/ui";

const EVENT_TYPES = [
  { value: "", label: "All Events" },
  { value: "delivered", label: "Delivered" },
  { value: "processed", label: "Processed" },
  { value: "bounce", label: "Bounced" },
  { value: "dropped", label: "Dropped" },
  { value: "deferred", label: "Deferred" },
  { value: "open", label: "Opened" },
  { value: "click", label: "Clicked" },
  { value: "spam_report", label: "Spam Report" },
  { value: "unsubscribe", label: "Unsubscribe" },
];

const EVENT_CONFIG = {
  delivered: { color: colors.success, icon: CheckCircle2, label: "Delivered" },
  processed: { color: colors.info || "#3b82f6", icon: Clock, label: "Processed" },
  bounce: { color: colors.danger, icon: XCircle, label: "Bounced" },
  dropped: { color: colors.danger, icon: Ban, label: "Dropped" },
  deferred: { color: colors.warning, icon: ArrowDownCircle, label: "Deferred" },
  open: { color: colors.warning, icon: Eye, label: "Opened" },
  click: { color: colors.accent, icon: MousePointer, label: "Clicked" },
  spam_report: { color: colors.danger, icon: AlertCircle, label: "Spam" },
  unsubscribe: { color: colors.textSecondary, icon: Ban, label: "Unsubscribed" },
  group_unsubscribe: { color: colors.textSecondary, icon: Ban, label: "Group Unsub" },
  group_resubscribe: { color: colors.success, icon: CheckCircle2, label: "Resub" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function EventBadge({ type }) {
  const cfg = EVENT_CONFIG[type] || { color: colors.textSecondary, icon: Clock, label: type };
  const Icon = cfg.icon;
  return (
    <Badge color={cfg.color} bg={`${cfg.color}18`}>
      <Icon size={10} />
      {cfg.label}
    </Badge>
  );
}

function ExpandedRow({ event }) {
  const details = [];
  if (event.reason) details.push({ label: "Reason", value: event.reason });
  if (event.smtpStatus) details.push({ label: "SMTP Status", value: event.smtpStatus });
  if (event.response) details.push({ label: "Response", value: event.response });
  if (event.sgMessageId) details.push({ label: "Message ID", value: event.sgMessageId });
  if (event.ip) details.push({ label: "IP Address", value: event.ip });
  if (event.userAgent) details.push({ label: "User Agent", value: event.userAgent });
  if (event.url) details.push({ label: "Link URL", value: event.url });

  if (details.length === 0) {
    return (
      <div style={{ padding: "8px 12px", fontSize: 12, color: colors.textSecondary, fontStyle: "italic" }}>
        No additional details available.
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 12px", display: "grid", gridTemplateColumns: "120px 1fr", gap: "4px 12px", fontSize: 12 }}>
      {details.map((d) => (
        <div key={d.label} style={{ display: "contents" }}>
          <span style={{ fontWeight: 600, color: colors.textSecondary }}>{d.label}:</span>
          <span style={{ color: colors.text, wordBreak: "break-all" }}>{d.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function SendGridActivity() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [type, setType] = useState(searchParams.get("type") || "");
  const [fromDate, setFromDate] = useState(searchParams.get("from") || "");
  const [toDate, setToDate] = useState(searchParams.get("to") || "");

  const [events, setEvents] = useState([]);
  const [messages, setMessages] = useState(null);
  const [sgApiAvailable, setSgApiAvailable] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const LIMIT = 50;

  const loadActivity = useCallback(async (newOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: LIMIT, offset: newOffset };
      if (email.trim()) params.email = email.trim();
      if (type) params.type = type;
      if (fromDate) params.from = new Date(fromDate).toISOString();
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        params.to = end.toISOString();
      }

      const data = await fetchEmailActivity(params);
      setEvents(newOffset === 0 ? data.events : [...events, ...data.events]);
      setMessages(data.messages);
      setSgApiAvailable(data.sgApiAvailable);
      setTotal(data.total);
      setOffset(newOffset);

      // Update URL params
      const sp = new URLSearchParams();
      if (email.trim()) sp.set("email", email.trim());
      if (type) sp.set("type", type);
      if (fromDate) sp.set("from", fromDate);
      if (toDate) sp.set("to", toDate);
      setSearchParams(sp, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [email, type, fromDate, toDate]);

  // Auto-load on mount if URL has params
  useEffect(() => {
    if (searchParams.get("email") || searchParams.get("type") || searchParams.get("from")) {
      loadActivity(0);
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    loadActivity(0);
  };

  const inputStyle = {
    padding: "8px 12px",
    border: `1px solid ${colors.border}`,
    borderRadius: 6,
    fontSize: 13,
    fontFamily: fonts.body,
    background: colors.bgCard,
    color: colors.text,
    outline: "none",
  };

  // Compute summary stats from webhook events
  const stats = {};
  for (const evt of events) {
    stats[evt.type] = (stats[evt.type] || 0) + 1;
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <button
          onClick={() => navigate("/newsletters")}
          style={{
            background: "none",
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            padding: "6px 10px",
            cursor: "pointer",
            color: colors.textSecondary,
            display: "flex",
            alignItems: "center",
          }}
        >
          <ArrowLeft size={16} />
        </button>
        <div>
          <h1 style={{ fontFamily: fonts.heading, fontSize: 24, fontWeight: 700, color: colors.text, margin: 0 }}>
            Email Activity
          </h1>
          <p style={{ fontFamily: fonts.body, fontSize: 13, color: colors.textSecondary, margin: "2px 0 0" }}>
            SendGrid delivery events {sgApiAvailable ? "· SendGrid API connected" : "· Webhook data"}
          </p>
        </div>
      </div>

      {/* Filters */}
      <form
        onSubmit={handleSearch}
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 20,
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <div style={{ flex: "1 1 220px" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, textTransform: "uppercase" }}>
            Recipient Email
          </label>
          <div style={{ position: "relative" }}>
            <Search size={14} color={colors.textSecondary} style={{ position: "absolute", left: 10, top: 10 }} />
            <input
              type="text"
              placeholder="e.g. louvinerva@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ ...inputStyle, width: "100%", paddingLeft: 32 }}
            />
          </div>
        </div>

        <div style={{ flex: "0 0 160px" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, textTransform: "uppercase" }}>
            Event Type
          </label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value)}
            style={{ ...inputStyle, width: "100%", cursor: "pointer" }}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div style={{ flex: "0 0 150px" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, textTransform: "uppercase" }}>
            From Date
          </label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ ...inputStyle, width: "100%" }}
          />
        </div>

        <div style={{ flex: "0 0 150px" }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: colors.textSecondary, marginBottom: 4, textTransform: "uppercase" }}>
            To Date
          </label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ ...inputStyle, width: "100%" }}
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            padding: "8px 20px",
            background: colors.accent,
            color: "#fff",
            border: "none",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            opacity: loading ? 0.7 : 1,
            height: 37,
          }}
        >
          {loading ? <Loader2 size={14} className="spin" /> : <Search size={14} />}
          Search
        </button>      </form>

      {/* Error */}
      {error && (
        <div style={{ padding: "12px 16px", background: `${colors.danger}10`, border: `1px solid ${colors.danger}30`, borderRadius: 8, marginBottom: 16, color: colors.danger, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {/* Summary stat cards — show when we have results */}
      {events.length > 0 && (
        <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
          {[
            { key: "delivered", icon: CheckCircle2, label: "Delivered", color: colors.success },
            { key: "bounce", icon: XCircle, label: "Bounced", color: colors.danger },
            { key: "dropped", icon: Ban, label: "Dropped", color: colors.danger },
            { key: "deferred", icon: ArrowDownCircle, label: "Deferred", color: colors.warning },
            { key: "open", icon: Eye, label: "Opened", color: colors.warning },
            { key: "click", icon: MousePointer, label: "Clicked", color: colors.accent },
          ]
            .filter((s) => stats[s.key])
            .map((s) => (
              <div
                key={s.key}
                style={{
                  padding: "10px 16px",
                  background: colors.bgCard,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 120,
                }}
              >
                <s.icon size={16} color={s.color} />
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: fonts.heading, color: colors.text }}>{stats[s.key]}</div>
                  <div style={{ fontSize: 11, color: colors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            ))}
        </div>
      )}

      {/* SendGrid API messages (if available) */}
      {messages && messages.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontFamily: fonts.heading, fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 10, display: "flex", alignItems: "center", gap: 6 }}>
            <RefreshCw size={14} color={colors.accent} />
            SendGrid Messages API
          </h3>
          <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th style={thStyle}>Recipient</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: "center", width: 50 }}>Opens</th>
                  <th style={{ ...thStyle, textAlign: "center", width: 50 }}>Clicks</th>
                  <th style={thStyle}>Last Event</th>
                </tr>
              </thead>
              <tbody>
                {messages.map((msg) => (
                  <tr key={msg.id} style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                    <td style={tdStyle}>{msg.email}</td>
                    <td style={{ ...tdStyle, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: colors.textSecondary }}>
                      {msg.subject}
                    </td>
                    <td style={tdStyle}>
                      <Badge
                        color={msg.status === "delivered" ? colors.success : msg.status === "not_delivered" ? colors.danger : colors.warning}
                        bg={`${msg.status === "delivered" ? colors.success : msg.status === "not_delivered" ? colors.danger : colors.warning}18`}
                      >
                        {msg.status}
                      </Badge>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", fontFamily: fonts.mono }}>{msg.opensCount}</td>
                    <td style={{ ...tdStyle, textAlign: "center", fontFamily: fonts.mono }}>{msg.clicksCount}</td>
                    <td style={{ ...tdStyle, color: colors.textSecondary, whiteSpace: "nowrap" }}>{fmtTime(msg.lastEventTime)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Webhook events table */}
      {events.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <h3 style={{ fontFamily: fonts.heading, fontSize: 15, fontWeight: 700, color: colors.text, margin: 0 }}>
              Webhook Events
              <span style={{ fontWeight: 400, fontSize: 12, color: colors.textSecondary, marginLeft: 8 }}>
                {total} total
              </span>
            </h3>
          </div>

          <div style={{ background: colors.bgCard, border: `1px solid ${colors.border}`, borderRadius: 8, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${colors.border}` }}>
                  <th style={{ ...thStyle, width: 28 }}></th>
                  <th style={thStyle}>Time</th>
                  <th style={thStyle}>Recipient</th>
                  <th style={thStyle}>Event</th>
                  <th style={thStyle}>Subject</th>
                  <th style={thStyle}>Details</th>
                </tr>
              </thead>
              <tbody>
                {events.map((evt) => {
                  const isExpanded = expandedId === evt.id;
                  const hasDetails = evt.reason || evt.smtpStatus || evt.response || evt.sgMessageId || evt.ip || evt.url;
                  return (
                    <React.Fragment key={evt.id}>
                      <tr style={{ borderBottom: isExpanded ? "none" : `1px solid ${colors.borderLight}`, cursor: hasDetails ? "pointer" : "default" }} onClick={() => hasDetails && setExpandedId(isExpanded ? null : evt.id)}>
                        <td style={{ ...tdStyle, width: 28, padding: "6px 4px 6px 10px" }}>
                          {hasDetails && (isExpanded ? <ChevronDown size={12} color={colors.textSecondary} /> : <ChevronRight size={12} color={colors.textSecondary} />)}
                        </td>
                        <td style={{ ...tdStyle, whiteSpace: "nowrap", color: colors.textSecondary, fontFamily: fonts.mono }}>
                          {fmtTime(evt.timestamp)}
                        </td>
                        <td style={{ ...tdStyle, fontWeight: 500, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {evt.email}
                        </td>
                        <td style={tdStyle}>
                          <EventBadge type={evt.type} />
                        </td>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: colors.textSecondary }}>
                          {evt.subject || "—"}
                        </td>
                        <td style={{ ...tdStyle, color: evt.reason ? colors.danger : colors.textSecondary, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {evt.reason || evt.smtpStatus || "—"}
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                          <td colSpan={6} style={{ padding: 0, background: `${colors.accent}06` }}>
                            <ExpandedRow event={evt} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Load more */}
          {events.length < total && (
            <div style={{ textAlign: "center", marginTop: 16 }}>
              <button
                onClick={() => loadActivity(offset + LIMIT)}
                disabled={loading}
                style={{
                  padding: "8px 24px",
                  background: "none",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.accent,
                  cursor: loading ? "not-allowed" : "pointer",
                }}
              >
                {loading ? "Loading..." : `Load More (${events.length} of ${total})`}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!loading && events.length === 0 && !error && (
        <div style={{ textAlign: "center", padding: "60px 20px", color: colors.textSecondary }}>
          <Search size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
            Search Email Activity
          </div>
          <div style={{ fontSize: 13 }}>
            Enter a recipient email or select filters to view delivery events from SendGrid.
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "8px 10px",
  color: colors.textSecondary,
  fontWeight: 600,
  fontSize: 11,
  textTransform: "uppercase",
  fontFamily: fonts.body,
};

const tdStyle = {
  padding: "8px 10px",
  color: colors.text,
};
